import {mergeAttributes, Node} from "@tiptap/core";
import type {CommandProps} from "@tiptap/core";
import {TextSelection} from "@tiptap/pm/state";
import {ReactNodeViewRenderer} from "@tiptap/react";
import {parseColorAttribute} from "../shared/blockAttributes";
import PrintoutTableNodeView from "./PrintoutTableNodeView";
import {
    canDeletePrintoutTableColumn,
    canDeletePrintoutTableRow,
    canInsertPrintoutTableColumn,
    canInsertPrintoutTableRow,
    deletePrintoutTableColumn,
    deletePrintoutTableRow,
    findAdjacentPrintoutTableCell,
    findPrintoutTable,
    findPrintoutTableCellDepth,
    insertPrintoutTableColumn,
    insertPrintoutTableRow,
    selectPrintoutTableCellContent,
} from "./printoutTableCommands";
import {
    DEFAULT_TABLE_BORDER_COLOR,
    PRINTOUT_TABLE_CELL_NODE_NAME,
    PRINTOUT_TABLE_HEADER_NODE_NAME,
    PRINTOUT_TABLE_NODE_NAME,
    PRINTOUT_TABLE_ROW_NODE_NAME,
    type PrintoutTableNodeAttrs,
} from "./printoutTableOptions";

// Fixed-grid schema: pasted spans normalize to 1 and new tables use plain cells.
declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        printoutTable: {
            addRowBefore: () => ReturnType;
            addRowAfter: () => ReturnType;
            deleteRow: () => ReturnType;
            addColumnBefore: () => ReturnType;
            addColumnAfter: () => ReturnType;
            deleteColumn: () => ReturnType;
        };
    }
}

const TABLE_CELL_ALIGNMENTS = ["left", "center", "right"] as const;

type TableBorderColorAttribute = keyof Pick<
    PrintoutTableNodeAttrs,
    "borderColor" | "borderTopColor" | "borderRightColor" | "borderBottomColor" | "borderLeftColor"
>;

const createTableBorderColorAttribute = (
    attribute: TableBorderColorAttribute,
    htmlAttribute: string,
    defaultValue: string | null = null,
) => ({
    default: defaultValue,
    parseHTML: (element: HTMLElement) => parseColorAttribute(element.getAttribute(htmlAttribute)),
    renderHTML: (attributes: Record<string, unknown>) => {
        const value = parseColorAttribute(attributes[attribute]);

        return value ? {[htmlAttribute]: value} : {};
    },
});

// Parse cell alignment from pasted HTML; new alignment lives on paragraphs.
const tableCellAlignAttribute = {
    default: null as string | null,
    parseHTML: (element: HTMLElement) => {
        const value = (element.style.textAlign || element.getAttribute("align") || "").trim().toLowerCase();

        return TABLE_CELL_ALIGNMENTS.includes(value as typeof TABLE_CELL_ALIGNMENTS[number])
            ? value
            : null;
    },
    renderHTML: (attributes: Record<string, unknown>) => (
        attributes.align ? {style: `text-align: ${attributes.align}`} : {}
    ),
};

const tableCellSpanAttributes = {
    colspan: {
        default: 1,
        parseHTML: () => 1,
    },
    rowspan: {
        default: 1,
        parseHTML: () => 1,
    },
    colwidth: {
        default: null as number[] | null,
        parseHTML: (element: HTMLElement) => {
            const value = element.getAttribute("colwidth");

            return value
                ? value.split(",").map(width => Number.parseInt(width, 10)).filter(Number.isFinite)
                : null;
        },
    },
    align: tableCellAlignAttribute,
};

const moveToAdjacentPrintoutTableCell = (direction: 1 | -1) => ({state, tr, dispatch}: CommandProps) => {
    const location = findPrintoutTable(state.selection);

    if (!location) return false;

    const cell = findAdjacentPrintoutTableCell(location, direction);

    // Keep Tab inside the document and never grow the table implicitly.
    if (!cell) return true;

    if (dispatch) selectPrintoutTableCellContent(tr, cell);

    return true;
};

// Keep boundary deletion from selecting content outside the table.
const keepPrintoutTableCellBoundary = (direction: 1 | -1) => ({state}: CommandProps) => {
    const {selection} = state;
    const {$from} = selection;

    if (!selection.empty) return false;

    const cellDepth = findPrintoutTableCellDepth($from);

    if (cellDepth === null) return false;

    return direction < 0
        ? $from.pos === $from.start(cellDepth) + 1
        : $from.pos === $from.end(cellDepth) - 1;
};

export const PrintoutTableNode = Node.create({
    name: PRINTOUT_TABLE_NODE_NAME,

    addOptions() {
        return {
            HTMLAttributes: {},
        };
    },

    content: `${PRINTOUT_TABLE_ROW_NODE_NAME}+`,

    group: "block",

    // Also tells palette drops to escape the table boundary.
    isolating: true,

    draggable: true,

    selectable: true,

    addAttributes() {
        return {
            backgroundColor: {
                default: null,
                parseHTML: (element: HTMLElement) => parseColorAttribute(element.getAttribute("data-background-color")),
                renderHTML: (attributes: Record<string, unknown>) => attributes.backgroundColor
                    ? {
                        "data-background-color": attributes.backgroundColor,
                        style: `background-color: ${attributes.backgroundColor}`,
                    }
                    : {},
            },
            borderColor: createTableBorderColorAttribute(
                "borderColor",
                "data-border-color",
                DEFAULT_TABLE_BORDER_COLOR,
            ),
            borderTopColor: createTableBorderColorAttribute("borderTopColor", "data-border-top-color"),
            borderRightColor: createTableBorderColorAttribute("borderRightColor", "data-border-right-color"),
            borderBottomColor: createTableBorderColorAttribute("borderBottomColor", "data-border-bottom-color"),
            borderLeftColor: createTableBorderColorAttribute("borderLeftColor", "data-border-left-color"),
            colWidths: {
                default: null,
                parseHTML: (element: HTMLElement) => {
                    const raw = element.getAttribute("data-col-widths");
                    if (!raw) return null;

                    try {
                        const parsed = JSON.parse(raw);

                        return Array.isArray(parsed) ? parsed : null;
                    } catch (error) {
                        console.warn("Failed to parse printout table column widths", error);

                        return null;
                    }
                },
                renderHTML: (attributes: Record<string, unknown>) => attributes.colWidths
                    ? {"data-col-widths": JSON.stringify(attributes.colWidths)}
                    : {},
            },
        };
    },

    parseHTML() {
        return [{tag: "table"}];
    },

    // tbody matches the node view's contentDOM for clipboard round-trips.
    renderHTML({HTMLAttributes}) {
        return [
            "table",
            mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
            ["tbody", 0],
        ];
    },

    addCommands() {
        return {
            addRowBefore: () => ({state, tr, dispatch}) => {
                const location = findPrintoutTable(state.selection);

                if (!location || !canInsertPrintoutTableRow(location)) return false;
                if (!dispatch) return true;

                return insertPrintoutTableRow(tr, location, location.rowIndex);
            },

            addRowAfter: () => ({state, tr, dispatch}) => {
                const location = findPrintoutTable(state.selection);

                if (!location || !canInsertPrintoutTableRow(location)) return false;
                if (!dispatch) return true;

                return insertPrintoutTableRow(tr, location, location.rowIndex + 1);
            },

            deleteRow: () => ({state, tr, dispatch}) => {
                const location = findPrintoutTable(state.selection);

                if (!location || !canDeletePrintoutTableRow(location)) return false;
                if (!dispatch) return true;

                return deletePrintoutTableRow(tr, location);
            },

            addColumnBefore: () => ({state, tr, dispatch}) => {
                const location = findPrintoutTable(state.selection);

                if (!location || !canInsertPrintoutTableColumn(location)) return false;
                if (!dispatch) return true;

                return insertPrintoutTableColumn(tr, location, location.cellIndex);
            },

            addColumnAfter: () => ({state, tr, dispatch}) => {
                const location = findPrintoutTable(state.selection);

                if (!location || !canInsertPrintoutTableColumn(location)) return false;
                if (!dispatch) return true;

                return insertPrintoutTableColumn(tr, location, location.cellIndex + 1);
            },

            deleteColumn: () => ({state, tr, dispatch}) => {
                const location = findPrintoutTable(state.selection);

                if (!location || !canDeletePrintoutTableColumn(location)) return false;
                if (!dispatch) return true;

                return deletePrintoutTableColumn(tr, location);
            },
        };
    },

    addKeyboardShortcuts() {
        return {
            Tab: () => this.editor.commands.command(moveToAdjacentPrintoutTableCell(1)),
            "Shift-Tab": () => this.editor.commands.command(moveToAdjacentPrintoutTableCell(-1)),
            Backspace: () => this.editor.commands.command(keepPrintoutTableCellBoundary(-1)),
            "Mod-Backspace": () => this.editor.commands.command(keepPrintoutTableCellBoundary(-1)),
            Delete: () => this.editor.commands.command(keepPrintoutTableCellBoundary(1)),
            "Mod-Delete": () => this.editor.commands.command(keepPrintoutTableCellBoundary(1)),
        };
    },

    addNodeView() {
        return ReactNodeViewRenderer(PrintoutTableNodeView, {
            contentDOMElementTag: "tbody",
            trackNodeViewPosition: true,
        });
    },
});

export const PrintoutTableRowNode = Node.create({
    name: PRINTOUT_TABLE_ROW_NODE_NAME,

    addOptions() {
        return {
            HTMLAttributes: {},
        };
    },

    content: `(${PRINTOUT_TABLE_CELL_NODE_NAME} | ${PRINTOUT_TABLE_HEADER_NODE_NAME})*`,

    parseHTML() {
        return [{tag: "tr"}];
    },

    renderHTML({HTMLAttributes}) {
        return ["tr", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
    },
});

// Restrict cells to the bounded text nodes used by printouts.
export const PrintoutTableCellNode = Node.create({
    name: PRINTOUT_TABLE_CELL_NODE_NAME,

    addOptions() {
        return {
            HTMLAttributes: {},
        };
    },

    content: "(paragraph | heading)+",

    isolating: true,

    addAttributes() {
        return {...tableCellSpanAttributes};
    },

    parseHTML() {
        return [{tag: "td"}];
    },

    renderHTML({HTMLAttributes}) {
        return ["td", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
    },
});

// Accept pasted th cells even though new tables create td cells.
export const PrintoutTableHeaderNode = Node.create({
    name: PRINTOUT_TABLE_HEADER_NODE_NAME,

    addOptions() {
        return {
            HTMLAttributes: {},
        };
    },

    content: "block+",

    isolating: true,

    addAttributes() {
        return {...tableCellSpanAttributes};
    },

    parseHTML() {
        return [{tag: "th"}];
    },

    renderHTML({HTMLAttributes}) {
        return ["th", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
    },
});
